import { FormSectionHandler } from '../entity.handler';
import { DeviceResponse, DeviceUpdateRequest, IdName } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, IdNameComboValueHandlers, SectionField, EMPTY_GUID, SearchSelectField } from '../utils';

interface EditorValues {
    asset?: IdName;
    simcard?: IdName;
}

export class DeviceLinkedSectionHandler implements FormSectionHandler<DeviceResponse, EditorValues> {

    type = 'form';
    icon = 'link';
    bulk = false;
    warning?: string;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    private properties = ['asset', 'simcard'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.DEVICE.LINKED.${key}`, values);
    }

    applyUpdate(entity: DeviceResponse, payload: DeviceUpdateRequest): Promise<DeviceResponse> {
        return this.admin.applyUpdate(api => api.entities.updateDevice(entity.id, payload), { _type: 'device' });
    }

    async mapEntityToEditor(entity: DeviceResponse): Promise<EditorValues> {
        if (entity?.asset) {
            // Show a warning when the linked asset has multiple devices assigned to it.
            const asset = await this.admin.getCached(`getAsset/${entity?.asset?.id}`, api => api.entities.getAsset(entity?.asset?.id), 10);
            this.warning = asset?.devices?.length > 1 ? this.translate('WARNING.ASSET_MULTIPLE_DEVICE') : null;
        }
        return Promise.resolve(EntityUtils.mergeFields(this.properties, entity, {}));
    }

    mapEditorToEntity(entity: DeviceUpdateRequest, values: EditorValues): Promise<DeviceUpdateRequest> {
        return Promise.resolve(EntityUtils.mergeFields(this.properties, values, entity));
    }

    async getFields(entity: DeviceResponse): Promise<SectionField[]> {
        return [
            {
                ...SearchSelectField('simcard', this.translate('FIELDS.SIM_CARD.TITLE'), false, {
                    searchItemPromise: id => this.admin.getCached(`getSimCard/${id}`, api => api.entities.getSimCard(id)),
                    searchListPromise: (limit, filter) => {
                        const f = 'state=active' + (!!filter.length ? `&name=*${filter}*` : '');
                        return this.admin.getCached(`listSimCards/${this.ownerId}/0/${limit}/${f}`, api => api.entities.listSimCards(this.ownerId, 0, limit, null, f)).then(res => ({ items: res.items, count: res.count }));
                    },
                }),
                placeholder: this.translate('FIELDS.SIM_CARD.PLACEHOLDER'),
                getHref: (field: FormBuilderField, values: any) => {
                    const val = values[field.id];
                    return val ? `/admin/client/${entity.owner.id}/simcards/grid/simcard/${val.id}` : null;
                },
            },
            {
                type: 'assetfilter', id: 'asset', title: this.translate('FIELDS.ASSET'), max: 1, required: false,
                options: { ownerId: this.ownerId },
                ...IdNameComboValueHandlers,
                getHref: (field: FormBuilderField, values: any) => {
                    const val = values[field.id];
                    return val ? `/admin/client/${entity.owner.id}/assets/grid/asset/${val.id}` : null;
                },
                setValue(field: FormBuilderField, values: any, value: any): void {
                    values[field.id] = {
                        id: value ? value.id : EMPTY_GUID,
                        name: value && value.name,
                    };
                },
            },
        ];
    }

}
